import React, { useState } from "react";
import { useIrAPadre } from "@/components/BotonVolver";
import { abrirVisor } from "@/lib/visor";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { DocAcciones } from "@/components/documentos/DocAcciones";
import { MatterGroups } from "@/components/MatterGroups";
import { Sheet, Input, nombreRel } from "@/pages/Familia";
import { VincularDocsMascota } from "@/components/VincularDocsMascota";
import { formatApiErrorDetail } from "@/lib/api";
import { eur } from "@/lib/gastos";
import { fechaCorta } from "@/lib/inicio";
const Field = ({ label, value }) =>
  value == null || value === "" ? null : (
    <div className="flex justify-between gap-4 px-4 py-3">
      <span className="text-base text-[#6E7A74]">{label}</span>
      <span className="text-base font-medium text-[#1C2421] text-right">{value}</span>
    </div>
  );

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="font-heading text-lg font-bold text-[#1C2421] flex items-center gap-2 mt-7 mb-3">
    <Icon size={20} className="text-[#3A6B56]" /> {children}
  </h2>
);

export default function MascotaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const irA = useIrAPadre();
  const { getPetById, getMattersByPet, getDocumentsForPet, getMemberById, familyMembers, updateMascota, deleteMascota, getDocumentoBlobUrl, eliminarDocumento, restaurarDocumento, gastos = [] } = useData();
  const pet = getPetById(id);
  const [edit, setEdit] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openingDoc, setOpeningDoc] = useState(null);
  const [showVincular, setShowVincular] = useState(false);
  const [confirmDelDoc, setConfirmDelDoc] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);

  if (!pet) {
    return (
      <div className="text-center py-20">
        <p className="text-lg text-[#6E7A74]">No se ha encontrado la mascota.</p>
        <button
          onClick={() => irA("/mascotas")}
          className="mt-4 min-h-[52px] px-6 rounded-xl bg-[#3A6B56] text-white font-semibold"
        >
          Volver a Mascotas
        </button>
      </div>
    );
  }

  const Icon = Icons[pet.icon] || Icons.PawPrint;
  const matters = getMattersByPet(pet.id);
  const docs = getDocumentsForPet(pet.id);
  const owner = getMemberById(pet.owner_member_id);

  const reminders = matters.flatMap((m) =>
    (m.reminders || []).map((r) => ({ ...r, matter: m.title }))
  );
  const history = [
    ...matters.flatMap((m) => (m.history || []).map((h) => ({ ...h, matter: m.title }))),
    ...((pet.historial || []).map((h) => ({ ...h, matter: `Ficha de ${pet.name}` }))),
  ].reverse();

  const openEdit = () => setEdit({
    name: pet.name, species: pet.species || "", breed: pet.breed || "",
    sex: pet.sex || "", birth_date: pet.birth_date || "", microchip: pet.microchip || "",
    passport_number: pet.passport_number || "",
    insurer: pet.insurer || "", vet: pet.vet || "", notes: pet.notes || "",
    owner_member_id: pet.owner_member_id || "fm-self",
  });

  const saveEdit = async () => {
    if (!edit.name.trim()) return toast.error("Indica el nombre.");
    setBusy(true);
    try {
      const sp = (edit.species || "").toLowerCase();
      const icon = sp.includes("gato") ? "Cat" : sp.includes("perro") ? "Dog" : "PawPrint";
      await updateMascota(pet.id, { ...edit, name: edit.name.trim(), icon });
      toast.success("Mascota actualizada");
      setEdit(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar.");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteMascota(pet.id);
      toast.success("Mascota eliminada");
      irA("/mascotas");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar.");
    } finally { setBusy(false); }
  };

  const openDoc = async (d) => {
    setOpeningDoc(d.id);
    try {
      abrirVisor(d);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo abrir el documento.");
    } finally {
      setOpeningDoc(null);
    }
  };

  const doDeleteDoc = async () => {
    setDeletingDoc(true);
    const docToDelete = confirmDelDoc;
    try {
      await eliminarDocumento(docToDelete.id);
      setConfirmDelDoc(null);
      toast.success("Documento eliminado", {
        description: docToDelete.filename,
        action: {
          label: "Deshacer",
          onClick: async () => {
            try {
              await restaurarDocumento(docToDelete.id);
              toast.success("Documento restaurado");
            } catch (err) {
              toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo restaurar.");
            }
          },
        },
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar el documento.");
    } finally { setDeletingDoc(false); }
  };

  return (
    <div className="space-y-2 pb-4">
      <button
        data-testid="mascota-detail-back"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-[#3A6B56] font-semibold text-base mb-2 min-h-[44px]"
      >
        <Icons.ChevronLeft size={22} /> Volver
      </button>

      {/* Cabecera */}
      <div
        className="aldia-fade-up bg-white border border-[#E5DFD5] rounded-2xl p-5 shadow-sm"
        data-testid={`mascota-detail-${pet.id}`}
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-16 h-16 rounded-2xl bg-[#E8F0EC] flex items-center justify-center">
            <Icon size={34} className="text-[#3A6B56]" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl font-bold text-[#1C2421]">{pet.name}</h1>
            <p className="text-base text-[#6E7A74]">
              {pet.species} · {pet.breed} · {pet.age_label}
            </p>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#2E5645] bg-[#E8F0EC] rounded-full px-2.5 py-1 mt-2">
              <Icons.Users size={13} /> Familia · {nombreRel(owner)}
            </span>
          </div>
        </div>
        <div className="flex gap-2.5 mt-4">
          <button data-testid="mascota-edit" onClick={openEdit} className="flex-1 min-h-[48px] rounded-xl bg-[#F2EDE4] text-[#1C2421] font-semibold text-base border border-[#E5DFD5] hover:bg-[#E5DFD5] flex items-center justify-center gap-2">
            <Icons.Pencil size={18} /> Editar
          </button>
          <button data-testid="mascota-delete" onClick={() => setConfirmDel(true)} className="flex-1 min-h-[48px] rounded-xl bg-[#FEF2F2] text-[#B91C1C] font-semibold text-base border border-[#FECACA] hover:bg-[#FEE2E2] flex items-center justify-center gap-2">
            <Icons.Trash2 size={18} /> Eliminar
          </button>
        </div>
      </div>

      {/* Datos básicos */}
      <SectionTitle icon={Icons.Info}>Datos básicos</SectionTitle>
      <div className="bg-white border border-[#E5DFD5] rounded-2xl divide-y divide-[#F2EDE4]">
        <Field label="Nombre" value={pet.name} />
        <Field label="Especie" value={pet.species} />
        <Field label="Raza" value={pet.breed} />
        <Field label="Sexo" value={pet.sex} />
        <Field label="Fecha de nacimiento" value={`${pet.birth_date} (${pet.age_label})`} />
        <Field label="Nº de microchip" value={pet.microchip} />
        <Field label="Nº de pasaporte" value={pet.passport_number} />
        <Field label="Veterinario habitual" value={pet.vet} />
        <Field label="Seguro" value={pet.insurer} />
        <Field label="Propietario" value={nombreRel(owner)} />
        <Field label="Notas" value={pet.notes} />
      </div>

      {/* Próximos eventos y asuntos pendientes */}
      <SectionTitle icon={Icons.CalendarClock}>Próximos eventos y asuntos</SectionTitle>
      <MatterGroups
        matters={matters}
        historico
        emptyText={`${pet.name} no tiene asuntos registrados todavía.`}
      />

      {/* Documentos asociados */}
      <SectionTitle icon={Icons.FileText}>Documentos asociados</SectionTitle>
      <button
        data-testid="mascota-vincular-docs"
        onClick={() => setShowVincular(true)}
        className="w-full mb-2.5 min-h-[48px] rounded-xl bg-[#E8F0EC] text-[#2E5645] font-semibold text-base border border-[#CDE3D6] hover:bg-[#DCEAE1] flex items-center justify-center gap-2"
      >
        <Icons.Link2 size={18} /> Vincular documentos existentes
      </button>
      {docs.length > 0 ? (
        <div className="space-y-2.5">
          {docs.map((d) => {
            const DIcon = d.type === "Imagen" ? Icons.Image : Icons.FileText;
            return (
              <div
                key={d.id}
                data-testid={`mascota-doc-${d.id}`}
                className="w-full flex flex-wrap items-center gap-3 bg-white border border-[#E5DFD5] rounded-xl p-3.5 hover:border-[#3A6B56] transition-colors"
              >
                <button
                  onClick={() => openDoc(d)}
                  disabled={openingDoc === d.id}
                  data-testid={`mascota-doc-open-${d.id}`}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left disabled:opacity-60"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#E8F0EC] flex items-center justify-center shrink-0">
                    <DIcon size={20} className="text-[#3A6B56]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-base text-[#1C2421] truncate">{d.filename}</p>
                    <p className="text-sm text-[#6E7A74]">{d.size_label || d.size} · {d.date_label}</p>
                  </div>
                  {openingDoc === d.id
                    ? <Icons.Loader2 size={18} className="text-[#4A5550] animate-spin shrink-0" />
                    : <Icons.Eye size={18} className="text-[#4A5550] shrink-0" />}
                </button>
                <button data-testid={`mascota-doc-delete-${d.id}`} onClick={() => setConfirmDelDoc(d)} aria-label="Eliminar documento"
                  className="w-10 h-10 rounded-lg bg-[#FEF2F2] flex items-center justify-center hover:bg-[#FEE2E2] shrink-0">
                  <Icons.Trash2 size={18} className="text-[#B91C1C]" />
                </button>
                <DocAcciones doc={d} testid={`mascota-doc-acc-${d.id}`} />
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-base text-[#6E7A74] bg-white border border-[#E5DFD5] rounded-xl p-4">
          Aún no hay documentos asociados a {pet.name}.
        </p>
      )}

      {gastos.some((g) => g.pet_id === pet.id) && (
        <>
          <SectionTitle icon={Icons.Receipt}>Gastos</SectionTitle>
          <div className="space-y-2" data-testid="mascota-gastos">
            {gastos.filter((g) => g.pet_id === pet.id).map((g) => (
              <div key={g.id} data-testid={`mascota-gasto-${g.id}`} className="bg-white border border-[#E5DFD5] rounded-xl p-3 flex justify-between gap-3">
                <span className="text-base text-[#1C2421]">{g.concepto || g.proveedor || "Gasto"}<span className="block text-sm text-[#6E7A74]">{fechaCorta(g.fecha, true)}</span></span>
                <span className="font-semibold text-[#1C2421]">{eur(g.importe)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Recordatorios */}
      <SectionTitle icon={Icons.BellRing}>Recordatorios</SectionTitle>
      {reminders.length > 0 ? (
        <div className="space-y-2.5">
          {reminders.map((r, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border border-[#E5DFD5] rounded-xl p-3.5">
              <Icons.Bell size={20} className="text-[#D97706] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-base text-[#1C2421]">{r.text}</p>
                <p className="text-sm text-[#6E7A74]">{r.date_label} · {r.matter}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-base text-[#6E7A74] bg-white border border-[#E5DFD5] rounded-xl p-4">
          No hay recordatorios programados.
        </p>
      )}

      {/* Historial */}
      <SectionTitle icon={Icons.History}>Historial</SectionTitle>
      <div className="bg-white border border-[#E5DFD5] rounded-2xl p-4">
        <ol className="relative border-l-2 border-[#E5DFD5] ml-2 space-y-5">
          {history.map((h, i) => (
            <li key={i} className="ml-5">
              <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-[#3A6B56]" />
              <p className="text-sm font-semibold text-[#6E7A74]">{h.date_label} · {h.matter}</p>
              <p className="text-base text-[#1C2421] leading-relaxed">{h.text}</p>
            </li>
          ))}
        </ol>
      </div>

      {edit && (
        <Sheet onClose={() => setEdit(null)} testid="mascota-edit-form">
          <h2 className="font-heading text-2xl font-bold text-[#1C2421] mb-5">Editar mascota</h2>
          <Input label="Nombre" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} testid="mascota-edit-name" />
          <Input label="Especie" value={edit.species} onChange={(v) => setEdit({ ...edit, species: v })} testid="mascota-edit-species" />
          <Input label="Raza" value={edit.breed} onChange={(v) => setEdit({ ...edit, breed: v })} testid="mascota-edit-breed" />
          <Input label="Sexo" value={edit.sex} onChange={(v) => setEdit({ ...edit, sex: v })} testid="mascota-edit-sex" />
          <Input label="Fecha de nacimiento" type="date" value={edit.birth_date} onChange={(v) => setEdit({ ...edit, birth_date: v })} testid="mascota-edit-birth" />
          <Input label="Nº de microchip" value={edit.microchip} onChange={(v) => setEdit({ ...edit, microchip: v })} testid="mascota-edit-chip" />
          <Input label="Nº de pasaporte" value={edit.passport_number} onChange={(v) => setEdit({ ...edit, passport_number: v })} testid="mascota-edit-passport" />
          <Input label="Seguro / aseguradora" value={edit.insurer} onChange={(v) => setEdit({ ...edit, insurer: v })} testid="mascota-edit-insurer" />
          <Input label="Veterinario habitual" value={edit.vet} onChange={(v) => setEdit({ ...edit, vet: v })} testid="mascota-edit-vet" />
          <Input label="Notas" value={edit.notes} onChange={(v) => setEdit({ ...edit, notes: v })} testid="mascota-edit-notes" />
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Propietario (familia)</label>
            <select data-testid="mascota-edit-owner" value={edit.owner_member_id} onChange={(e) => setEdit({ ...edit, owner_member_id: e.target.value })}
              className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/20 focus:border-[#3A6B56]">
              {familyMembers.map((f) => <option key={f.id} value={f.id}>{nombreRel(f)}</option>)}
            </select>
          </div>
          <button data-testid="mascota-edit-save" onClick={saveEdit} disabled={busy} className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] disabled:opacity-60">{busy ? "Guardando…" : "Guardar cambios"}</button>
        </Sheet>
      )}

      {confirmDel && (
        <Sheet onClose={() => setConfirmDel(false)} testid="mascota-confirm-delete">
          <h2 className="font-heading text-xl font-bold text-[#1C2421]">¿Eliminar a {pet.name}?</h2>
          <p className="text-base text-[#6E7A74] mt-2">Se eliminará la ficha de la mascota. Esta acción no se puede deshacer.</p>
          <div className="flex gap-2.5 mt-5">
            <button onClick={() => setConfirmDel(false)} className="flex-1 min-h-[52px] rounded-xl bg-[#F2EDE4] font-semibold text-base border border-[#E5DFD5]">Cancelar</button>
            <button data-testid="mascota-confirm-delete-btn" onClick={doDelete} disabled={busy} className="flex-1 min-h-[52px] rounded-xl bg-[#B91C1C] text-white font-semibold text-base disabled:opacity-60">{busy ? "Eliminando…" : "Eliminar"}</button>
          </div>
        </Sheet>
      )}

      {showVincular && <VincularDocsMascota pet={pet} onClose={() => setShowVincular(false)} />}

      {confirmDelDoc && (
        <Sheet onClose={() => setConfirmDelDoc(null)} testid="mascota-doc-confirm-delete">
          <h2 className="font-heading text-xl font-bold text-[#1C2421]">¿Eliminar este documento?</h2>
          <p className="text-base text-[#6E7A74] mt-2">Se eliminará «{confirmDelDoc.filename}». Podrás recuperarlo desde la Papelera durante 30 días.</p>
          <div className="flex gap-2.5 mt-5">
            <button onClick={() => setConfirmDelDoc(null)} className="flex-1 min-h-[52px] rounded-xl bg-[#F2EDE4] font-semibold text-base border border-[#E5DFD5]">Cancelar</button>
            <button data-testid="mascota-doc-confirm-delete-btn" onClick={doDeleteDoc} disabled={deletingDoc} className="flex-1 min-h-[52px] rounded-xl bg-[#B91C1C] text-white font-semibold text-base disabled:opacity-60">{deletingDoc ? "Eliminando…" : "Eliminar"}</button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
